// PDF skim-report generator (jsPDF, A4 portrait). PRD §7 Feature 5.
//
// Audience: the board editor and the chief editor they forward it to. The
// report leads with the recommendation, then the evidence behind it
// (study-type/guideline, methodology gaps, integrity flags, a reference
// audit, and COI-screened reviewer suggestions). Advisory throughout — the
// editor's decision is final.

import { jsPDF } from 'jspdf';
import {
  DECISION_LABELS,
  explainDecision,
  type DecisionLabel,
  type ExportInput,
} from './types';
import { loadGuidelineById } from '../checklists';

const APP_VERSION = '1.0.0';

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN_X = 18;
const MARGIN_Y = 18;
const MAX_WIDTH = PAGE_WIDTH - 2 * MARGIN_X;
const LINE = 5;

const RED: [number, number, number] = [185, 28, 28];
const AMBER: [number, number, number] = [180, 83, 9];
const GREEN: [number, number, number] = [21, 128, 61];
const GREY: [number, number, number] = [110, 110, 110];
const INK: [number, number, number] = [17, 24, 39];

interface C {
  pdf: jsPDF;
  y: number;
}

// jsPDF core fonts are WinAnsi only; normalise the typographic characters
// common in manuscript metadata (curly quotes, en/em/figure dashes,
// ellipsis, bullets) and strip zero-width chars so the report doesn't render
// boxes/garbage. NBSP / figure / narrow spaces are folded by the final \s+.
// All patterns use \u escapes so the source stays ASCII / lint-clean.
function clean(s: string): string {
  return (s ?? '')
    .replace(/[‘’‚′]/g, "'")
    .replace(/[“”„″]/g, '"')
    .replace(/[‐‑‒–—―−]/g, '-')
    .replace(/…/g, '...')
    .replace(/\u200B|\u200C|\u200D|\u2060|\uFEFF/g, '')
    .replace(/[•●]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function space(c: C, needed: number): void {
  if (c.y + needed > PAGE_HEIGHT - MARGIN_Y - 6) {
    c.pdf.addPage();
    c.y = MARGIN_Y;
  }
}

function setColor(c: C, rgb: [number, number, number]): void {
  c.pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
}

function heading(c: C, text: string, count?: number): void {
  space(c, 12);
  c.y += 3;
  c.pdf.setFont('helvetica', 'bold');
  c.pdf.setFontSize(11);
  setColor(c, INK);
  const label = count !== undefined ? `${text}  (${count})` : text;
  c.pdf.text(label, MARGIN_X, c.y);
  c.y += 2;
  c.pdf.setDrawColor(210, 210, 210);
  c.pdf.line(MARGIN_X, c.y, PAGE_WIDTH - MARGIN_X, c.y);
  c.y += 4.5;
  c.pdf.setFont('helvetica', 'normal');
  c.pdf.setFontSize(10);
  setColor(c, INK);
}

function para(
  c: C,
  text: string,
  opts: { size?: number; color?: [number, number, number]; bold?: boolean; indent?: number } = {},
): void {
  if (!text) return;
  const size = opts.size ?? 10;
  const indent = opts.indent ?? 0;
  c.pdf.setFont('helvetica', opts.bold ? 'bold' : 'normal');
  c.pdf.setFontSize(size);
  setColor(c, opts.color ?? INK);
  const lines = c.pdf.splitTextToSize(clean(text), MAX_WIDTH - indent) as string[];
  const lh = size <= 9 ? 4.3 : LINE;
  for (const ln of lines) {
    space(c, lh);
    c.pdf.text(ln, MARGIN_X + indent, c.y);
    c.y += lh;
  }
  c.pdf.setFont('helvetica', 'normal');
  c.pdf.setFontSize(10);
  setColor(c, INK);
}

function bullet(
  c: C,
  text: string,
  opts: { color?: [number, number, number]; tag?: string } = {},
): void {
  const tag = opts.tag ? `[${opts.tag}] ` : '';
  const lines = c.pdf.splitTextToSize(clean(tag + text), MAX_WIDTH - 5) as string[];
  for (let i = 0; i < lines.length; i++) {
    space(c, 4.6);
    setColor(c, i === 0 && opts.color ? opts.color : INK);
    c.pdf.text((i === 0 ? '- ' : '  ') + lines[i], MARGIN_X + 2, c.y);
    c.y += 4.6;
  }
  setColor(c, INK);
}

function gap(c: C, n = 2): void {
  c.y += n;
}

function decisionColor(d: DecisionLabel | null): [number, number, number] {
  if (d === 'desk_reject' || d === 'reject_with_feedback') return RED;
  if (d === 'send_for_review_after_revisions') return AMBER;
  if (d === 'send_for_review') return GREEN;
  return GREY;
}

function sevColor(sev: string): [number, number, number] {
  if (sev === 'red') return RED;
  if (sev === 'amber') return AMBER;
  return GREY;
}

export function generatePdf(input: ExportInput): Blob {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const c: C = { pdf, y: MARGIN_Y };
  const m = input.manuscript;
  const cover = m.emCover ?? null;

  // ---- Header / manuscript identity -------------------------------------
  const title = cover?.fullTitle || m.title || input.fileName;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  setColor(c, INK);
  for (const ln of pdf.splitTextToSize(clean(title), MAX_WIDTH) as string[]) {
    space(c, 7);
    pdf.text(ln, MARGIN_X, c.y);
    c.y += 7;
  }
  c.y += 1;
  const articleType = m.articleType || cover?.articleType || null;
  const idLine = [
    articleType ? clean(articleType) : null,
    `File: ${clean(input.fileName)}`,
    `${m.wordCount.toLocaleString()} words`,
    `${m.pages.length} pp`,
  ]
    .filter(Boolean)
    .join('  -  ');
  para(c, idLine, { size: 9, color: GREY });

  const authors = cover?.authors?.length
    ? cover.authors
    : m.authorSurnames.map((s) => s.charAt(0).toUpperCase() + s.slice(1));
  if (authors.length) para(c, `Authors: ${authors.join(', ')}`, { size: 9, color: GREY });
  const affils = cover?.affiliations?.length ? cover.affiliations : m.affiliations;
  if (affils.length) para(c, `Affiliations: ${affils.join('; ')}`, { size: 9, color: GREY });
  if (cover?.keywords?.length)
    para(c, `Keywords: ${cover.keywords.join('; ')}`, { size: 9, color: GREY });

  c.y += 1;
  pdf.setDrawColor(150, 150, 150);
  pdf.line(MARGIN_X, c.y, PAGE_WIDTH - MARGIN_X, c.y);
  c.y += 3;
  para(
    c,
    `Editorial skim report - generated ${clean(input.generatedAt)} - ManuscriptSkim ${APP_VERSION}. ` +
      `Advisory: pattern-engine assisted; the editor's decision is final.`,
    { size: 8, color: GREY },
  );

  // ---- Recommendation (lead) -------------------------------------------
  heading(c, 'Recommendation');
  const dlabel = input.draft.decision
    ? DECISION_LABELS[input.draft.decision]
    : 'Pending - editor to confirm';
  para(c, dlabel, { size: 13, bold: true, color: decisionColor(input.draft.decision) });
  if (input.draft.rationale.trim()) {
    gap(c);
    para(c, input.draft.rationale.trim());
  } else {
    // No editor-written rationale yet — show the rubric's basis so the
    // chief editor sees *why* this is recommended.
    para(c, explainDecision(input.pattern, { referenceAudit: input.referenceAudit }), {
      size: 9,
      color: GREY,
    });
  }

  // ---- Study type & guideline ------------------------------------------
  const cl = input.pattern.classifier;
  heading(c, 'Study type & reporting guideline');
  para(c, `${cl.label} - ${Math.round(cl.confidence * 100)}% confidence`, { bold: true });
  const meth = input.pattern.methodology;
  if (meth) {
    const g = loadGuidelineById(meth.guidelineId);
    const vs = g?.verification_status;
    const vnote =
      vs === 'verified'
        ? ' (checklist source-verified verbatim)'
        : vs === 'verified_with_consolidations'
          ? ' (source-verified; some sub-items consolidated)'
          : vs === 'training_data_best_effort'
            ? ' (NB: checklist text best-effort, not source-verified)'
            : vs === 'draft'
              ? ' (NB: checklist encoding is draft)'
              : '';
    para(c, `Applicable guideline: ${meth.guidelineId} v${meth.guidelineVersion}${vnote}`);
  } else if (cl.noFormalGuideline) {
    para(
      c,
      'No formal EQUATOR guideline applies to this article type (assess against ICMJE / journal norms).',
    );
  } else {
    para(
      c,
      'No EQUATOR checklist is encoded for this design. Assess against the appropriate ' +
        'reporting guideline (e.g. PRISMA-DTA, TRIPOD+AI, CHEERS, SRQR/COREQ, ARRIVE, SPIRIT) ' +
        'and journal norms.',
    );
  }
  if (cl.confidence < 0.75)
    para(c, 'Low confidence - please confirm the study type before relying on the checklist.', {
      size: 9,
      color: AMBER,
    });
  if (cl.alternatives.length)
    para(c, `Alternatives considered: ${cl.alternatives.map((a) => a.label).join(', ')}`, {
      size: 9,
      color: GREY,
    });
  if (cl.rationale) para(c, clean(cl.rationale), { size: 9, color: GREY });

  // ---- Methodology assessment ------------------------------------------
  if (meth) {
    const located = meth.items.filter((i) => i.status === 'located').length;
    const total = meth.items.length;
    const missingRed = meth.items.filter(
      (i) => i.status === 'not_located' && i.severityIfMissing === 'red',
    );
    const missingAmber = meth.items.filter(
      (i) => i.status === 'not_located' && i.severityIfMissing === 'amber',
    );
    heading(c, `Methodology - ${meth.guidelineId}`);
    para(
      c,
      `${located} of ${total} checklist items located by the pattern engine (${Math.round(
        meth.locatedFraction * 100,
      )}%). Located = evidence found for the editor to judge; not a compliance verdict.`,
    );
    if (missingRed.length) {
      gap(c);
      para(c, `High-severity items not located (${missingRed.length}):`, { bold: true });
      for (const it of missingRed.slice(0, 18))
        bullet(c, `${it.itemId}: ${it.itemText}`, { color: RED });
    }
    if (missingAmber.length)
      para(c, `Plus ${missingAmber.length} moderate-severity item(s) not located.`, {
        size: 9,
        color: GREY,
      });
  }

  // ---- Integrity findings (non-methodology) ----------------------------
  const integrity = input.pattern.findings.filter(
    (f) => f.category !== 'methodology' && (f.severity === 'red' || f.severity === 'amber'),
  );
  const groups: Record<string, typeof integrity> = {};
  for (const f of integrity) (groups[f.category] ??= []).push(f);
  const GROUP_TITLES: Record<string, string> = {
    stats: 'Statistical / numerical',
    references: 'Reference signals',
    voice: 'Voice / language patterns',
    format: 'Formatting',
  };
  if (integrity.length) {
    heading(c, 'Integrity findings', integrity.length);
    for (const cat of Object.keys(groups)) {
      para(c, GROUP_TITLES[cat] ?? cat, { bold: true, size: 10 });
      for (const f of groups[cat].slice(0, 15)) {
        bullet(c, f.findingText, { tag: f.severity.toUpperCase(), color: sevColor(f.severity) });
        if (f.evidencePassage)
          para(c, `"${f.evidencePassage.slice(0, 240)}"`, {
            size: 8,
            color: GREY,
            indent: 4,
          });
      }
      gap(c);
    }
    para(
      c,
      'Voice / pattern findings are observed signals to verify editorially - not AI-detection scores.',
      { size: 8, color: GREY },
    );
  }

  // ---- Reference audit --------------------------------------------------
  const ra = input.referenceAudit;
  if (ra && ra.entries.length) {
    const n = ra.entries.length;
    const by = (s: string) => ra.entries.filter((e) => e.outcome.status === s);
    const resolved = by('resolved').length;
    const retracted = by('retracted');
    const mismatch = by('mismatch');
    const notFound = ra.entries.filter(
      (e) => e.outcome.status === 'unresolved' && e.outcome.reason === 'doi_not_found',
    );
    const noDoi = ra.entries.filter(
      (e) => e.outcome.status === 'unresolved' && e.outcome.reason === 'no_doi',
    ).length;
    heading(c, 'Reference audit (Crossref)');
    para(
      c,
      `${n} references checked - ${resolved} resolved - ${retracted.length} retracted - ` +
        `${mismatch.length} metadata mismatch - ${notFound.length} DOI not found - ${noDoi} no DOI` +
        (ra.degraded ? ' - Crossref partially unreachable (audit incomplete)' : ''),
    );
    if (retracted.length) {
      gap(c);
      para(c, 'RETRACTED references cited:', { bold: true, color: RED });
      for (const e of retracted.slice(0, 20))
        bullet(
          c,
          `Ref ${e.referenceNumber ?? '?'}: ${e.doi ?? ''} - ${clean(e.rawTitle).slice(0, 160)}`,
          { color: RED },
        );
    }
    if (mismatch.length) {
      gap(c);
      para(c, 'Metadata mismatches (possible citation error):', { bold: true, color: AMBER });
      for (const e of mismatch.slice(0, 20)) {
        const ms = e.outcome.status === 'mismatch' ? e.outcome.mismatches.join('; ') : '';
        bullet(c, `Ref ${e.referenceNumber ?? '?'}: ${e.doi ?? ''} - ${ms}`, { color: AMBER });
      }
    }
    if (notFound.length) {
      gap(c);
      para(c, 'DOIs that did not resolve on Crossref:', { bold: true, color: AMBER });
      for (const e of notFound.slice(0, 20))
        bullet(c, `Ref ${e.referenceNumber ?? '?'}: ${e.doi ?? '(none)'}`, { color: AMBER });
    }
  }

  // ---- Suggested reviewers ---------------------------------------------
  const selected = new Set(input.draft.selectedReviewerKeys);
  const fromApi = input.reviewers?.candidates.filter((r) => selected.has(r.key)) ?? [];
  const reviewers = [...fromApi, ...input.draft.manualReviewers];
  if (reviewers.length) {
    heading(c, 'Suggested reviewers', reviewers.length);
    para(
      c,
      "Screened for conflicts against the manuscript's authors and affiliations. Verify independence before inviting.",
      { size: 8, color: GREY },
    );
    gap(c);
    for (const r of reviewers) {
      const head = [r.name, r.affiliation, r.orcid ? `ORCID ${r.orcid}` : null, r.email]
        .filter(Boolean)
        .join(' - ');
      bullet(c, head);
      if (r.relevantPaperTitle)
        para(
          c,
          `via: ${clean(r.relevantPaperTitle).slice(0, 160)}${
            r.relevantPaperYear ? ` (${r.relevantPaperYear})` : ''
          } - ${r.source}`,
          { size: 8, color: GREY, indent: 4 },
        );
    }
  }

  // ---- Editor notes (internal) -----------------------------------------
  if (input.draft.editorNotes.trim()) {
    heading(c, 'Editor notes (internal)');
    para(c, 'Not shown to the chief editor unless you include it in the email body.', {
      size: 8,
      color: GREY,
    });
    gap(c);
    para(c, input.draft.editorNotes.trim());
  }

  // ---- Footer on every page --------------------------------------------
  const pageCount = pdf.getNumberOfPages();
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'normal');
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    pdf.setTextColor(GREY[0], GREY[1], GREY[2]);
    pdf.setDrawColor(220, 220, 220);
    pdf.line(MARGIN_X, PAGE_HEIGHT - 12, PAGE_WIDTH - MARGIN_X, PAGE_HEIGHT - 12);
    pdf.text(
      `ManuscriptSkim ${APP_VERSION} - skim ${input.skimReportId.slice(0, 8)} - advisory - editor's decision is final`,
      MARGIN_X,
      PAGE_HEIGHT - 8,
    );
    pdf.text(`${p} / ${pageCount}`, PAGE_WIDTH - MARGIN_X, PAGE_HEIGHT - 8, { align: 'right' });
  }

  return pdf.output('blob');
}
